import { useLayoutEffect, useRef, type RefObject } from 'react';
import { motionAllowed } from '../lib/motion';

// --motion-enter and --ease-out (theme.css): quick to start, then a soft settle.
const GLIDE = { duration: 260, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' };

/**
 * Lets an element sized by its content, such as a button whose label changes, glide to its new
 * width when `content` changes, rather than jumping to it. A change mid-glide carries on from
 * where the glide had got to. The element should hide its overflow, as its new content may be
 * wider than it is until the glide is over.
 */
export function useWidthGlide(ref: RefObject<HTMLElement | null>, content: unknown): void {
  // The width as last drawn: layout keeps it, so a font arriving late doesn't leave it stale.
  const drawn = useRef(0);
  const glide = useRef<Animation | null>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      const size = entry?.borderBoxSize[0];
      if (size) drawn.current = size.inlineSize;
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [ref]);

  useLayoutEffect(() => {
    const element = ref.current;
    const from = drawn.current;
    if (!element || from === 0 || !motionAllowed()) return;
    glide.current?.cancel();
    // Its width without the glide, and unscaled by a press.
    const to = parseFloat(getComputedStyle(element).width);
    if (!to || Math.abs(to - from) < 0.5) return;
    glide.current = element.animate([{ width: `${from}px` }, { width: `${to}px` }], GLIDE);
  }, [ref, content]);
}
